import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAcknowledgementAuditTrailDetail,
  getAcknowledgementAuditTrailTimeline,
} from '../services/acknowledgementAuditTrailService';
import { AUDIT_TRAIL_TIMELINE_LIMIT } from '../constants/acknowledgementAuditTrail.constants';
import { filterTimelineByTab } from '../utils/acknowledgementAuditTrailUtils';
import type {
  AuditTimelineEntry,
  AuditTrailDetail,
  AuditTrailDetailContext,
  AuditTrailHistoryTab,
} from '../types/acknowledgementAuditTrail.types';

interface UseAcknowledgementAuditTrailDetailOptions {
  open: boolean;
  context: AuditTrailDetailContext | null;
  initialTab?: AuditTrailHistoryTab;
}

export function useAcknowledgementAuditTrailDetail({
  open,
  context,
  initialTab = 'auditTimeline',
}: UseAcknowledgementAuditTrailDetailOptions) {
  const [detail, setDetail] = useState<AuditTrailDetail | null>(null);
  const [timelineEvents, setTimelineEvents] = useState<AuditTimelineEntry[]>([]);
  const [activeTab, setActiveTab] = useState<AuditTrailHistoryTab>(initialTab);
  const [detailLoading, setDetailLoading] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [timelineError, setTimelineError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setDetail(null);
    setTimelineEvents([]);
    setActiveTab(initialTab);
    setDetailLoading(false);
    setTimelineLoading(false);
    setDetailError(null);
    setTimelineError(null);
  }, [initialTab]);

  const loadDetail = useCallback(async () => {
    if (!context) {
      return;
    }
    setDetailLoading(true);
    setDetailError(null);
    const result = await getAcknowledgementAuditTrailDetail(
      context.notificationId,
      context.facilityId
    );
    if (!result.success || !result.data) {
      setDetail(null);
      setDetailError(result.error || 'Failed to load audit detail');
    } else {
      setDetail(result.data);
    }
    setDetailLoading(false);
  }, [context]);

  const loadTimeline = useCallback(async () => {
    if (!context) {
      return;
    }
    setTimelineLoading(true);
    setTimelineError(null);
    const result = await getAcknowledgementAuditTrailTimeline(
      context.notificationId,
      context.facilityId,
      { limit: AUDIT_TRAIL_TIMELINE_LIMIT, offset: 0, sortOrder: 'ASC' }
    );
    if (!result.success) {
      setTimelineEvents([]);
      setTimelineError(result.error || 'Failed to load audit timeline');
    } else {
      setTimelineEvents(result.data);
    }
    setTimelineLoading(false);
  }, [context]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    void loadDetail();
    void loadTimeline();
  }, [open, context?.notificationId, context?.facilityId, loadDetail, loadTimeline, resetState]);

  const activeEvents = useMemo(
    () => filterTimelineByTab(timelineEvents, activeTab),
    [timelineEvents, activeTab]
  );

  const refresh = useCallback(async () => {
    await Promise.all([loadDetail(), loadTimeline()]);
  }, [loadDetail, loadTimeline]);

  return {
    detail,
    timelineEvents,
    activeTab,
    setActiveTab,
    activeEvents,
    detailLoading,
    timelineLoading,
    detailError,
    timelineError,
    refresh,
    eventCount: timelineEvents.length,
  };
}
